import { Controller } from "@hotwired/stimulus"

const SPACING = 28
// Brand gradient stops, left to right (teal, cyan, blue, violet, gold)
const STOPS = [[28, 201, 140], [36, 203, 222], [87, 169, 247], [189, 82, 249], [235, 179, 71]]
// Dots stay clear of the photo frame: none within CLEAR_GAP px of it (that is
// its glow), easing back to full strength over the next CLEAR_FADE px.
const CLEAR_GAP = 48
const CLEAR_FADE = 120
// Refuse to allocate a canvas bigger than this on either side (see #resize)
const MAX_SIDE = 8192

// Ambient dot field behind the hero. A slow diagonal wave lifts dots toward the
// brand gradient, and dots near the pointer swell. Draws once, statically, when
// the visitor prefers reduced motion. Pauses while off screen or in a hidden tab.
export default class extends Controller {
  connect() {
    this.ctx = this.element.getContext("2d")
    this.host = this.element.parentElement
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.pointer = null
    this.running = false

    // Layout is pinned here as well as in the stylesheet. If the CSS is late,
    // stale or missing, an in-flow canvas that is sized from its own layout box
    // grows without bound (each resize multiplies it by the pixel ratio) and
    // freezes the page. Absolutely positioned, it can never affect layout.
    Object.assign(this.element.style, { position: "absolute", inset: "0", width: "100%", height: "100%" })

    this.onResize = () => this.#resize()
    this.onMove = (e) => {
      const r = this.host.getBoundingClientRect()
      this.pointer = { x: e.clientX - r.left, y: e.clientY - r.top }
    }
    this.onLeave = () => { this.pointer = null }
    this.onVisibility = () => (document.hidden ? this.#stop() : this.#start())

    this.resizeObserver = new ResizeObserver(this.onResize)
    this.resizeObserver.observe(this.host)

    if (!this.reduced) {
      this.host.addEventListener("pointermove", this.onMove)
      this.host.addEventListener("pointerleave", this.onLeave)
      document.addEventListener("visibilitychange", this.onVisibility)
      this.intersection = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting
        this.inView ? this.#start() : this.#stop()
      })
      this.intersection.observe(this.element)
    }
  }

  disconnect() {
    this.#stop()
    this.resizeObserver?.disconnect()
    this.intersection?.disconnect()
    this.host.removeEventListener("pointermove", this.onMove)
    this.host.removeEventListener("pointerleave", this.onLeave)
    document.removeEventListener("visibilitychange", this.onVisibility)
  }

  // The drawing space is the host's box, measured from the host and never from
  // the canvas, so the backing store cannot feed back into its own size.
  #resize() {
    const w = this.host.clientWidth
    const h = this.host.clientHeight
    if (!w || !h || w > MAX_SIDE || h > MAX_SIDE) return

    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    this.w = w
    this.h = h
    this.element.width = Math.round(w * dpr)
    this.element.height = Math.round(h * dpr)
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    this.#draw(performance.now())
  }

  #start() {
    if (this.running || this.reduced || !this.inView || document.hidden) return
    this.running = true
    const tick = (t) => {
      if (!this.running) return
      this.#draw(t)
      this.raf = requestAnimationFrame(tick)
    }
    this.raf = requestAnimationFrame(tick)
  }

  #stop() {
    this.running = false
    cancelAnimationFrame(this.raf)
  }

  #draw(t) {
    const { ctx, w, h, pointer } = this
    ctx.clearRect(0, 0, w, h)
    const phase = this.reduced ? 0 : t * 0.0006
    const frame = this.#frameRect()

    for (let x = SPACING / 2; x < w; x += SPACING) {
      const [r, g, b] = this.#colorAt(x / w)
      for (let y = SPACING / 2; y < h; y += SPACING) {
        let clearance = 1
        if (frame) {
          const dx = Math.max(frame.left - x, 0, x - frame.right)
          const dy = Math.max(frame.top - y, 0, y - frame.bottom)
          clearance = Math.min(Math.max((Math.hypot(dx, dy) - CLEAR_GAP) / CLEAR_FADE, 0), 1)
          if (clearance === 0) continue
        }

        const wave = this.reduced ? 0 : (Math.sin((x + y) * 0.012 - phase) + 1) / 2
        let near = 0
        if (pointer) {
          const d = Math.hypot(x - pointer.x, y - pointer.y)
          if (d < 140) near = 1 - d / 140
        }
        const tint = Math.max(near, wave ** 6)
        const radius = 1.1 + wave * 0.7 + near * 2.4

        ctx.beginPath()
        ctx.arc(x, y, radius, 0, Math.PI * 2)
        ctx.fillStyle = tint < 0.05
          ? `rgba(29, 29, 29, ${(0.1 + wave * 0.08) * clearance})`
          : `rgba(${r}, ${g}, ${b}, ${(0.18 + tint * 0.6) * clearance})`
        ctx.fill()
      }
    }
  }

  // The photo frame's box in drawing coordinates (read live, so it follows the
  // entrance animation and any layout, including the stacked mobile one)
  #frameRect() {
    const frame = this.host.querySelector(".mk-frame-wrap")
    if (!frame) return null
    const origin = this.host.getBoundingClientRect()
    const box = frame.getBoundingClientRect()
    return {
      left: box.left - origin.left, right: box.right - origin.left,
      top: box.top - origin.top, bottom: box.bottom - origin.top,
    }
  }

  // Linear blend across the gradient stops for a 0..1 position
  #colorAt(p) {
    const scaled = Math.min(Math.max(p, 0), 1) * (STOPS.length - 1)
    const i = Math.min(Math.floor(scaled), STOPS.length - 2)
    const f = scaled - i
    return STOPS[i].map((c, k) => Math.round(c + (STOPS[i + 1][k] - c) * f))
  }
}
